import { Stack, TextField as MuiTextField } from '@mui/material';
import { useRef } from 'react';
import { type Control, Controller, type FieldPath, type FieldValues } from 'react-hook-form';

export interface OtpFieldProps<T extends FieldValues> {
  name: FieldPath<T>;
  control: Control<T>;
  length?: number;
}

/**
 * A segmented OTP input (email verification, MFA challenge — `docs/development/FrontendGuidelines.md` §8.2).
 * The RHF field value is always the joined string (e.g. "123456"); this component only owns the
 * per-box display and auto-advance/backspace interaction.
 */
export function OtpField<T extends FieldValues>({ name, control, length = 6 }: OtpFieldProps<T>) {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const digits = (field.value ?? '').padEnd(length, ' ').split('').slice(0, length);

        const setDigit = (index: number, char: string) => {
          const next = [...digits];
          next[index] = char;
          field.onChange(next.join('').trimEnd());
        };

        return (
          <Stack direction="row" spacing={1}>
            {digits.map((digit, index) => (
              <MuiTextField
                key={index}
                inputRef={(el: HTMLInputElement | null) => {
                  inputRefs.current[index] = el;
                }}
                value={digit.trim()}
                error={Boolean(fieldState.error)}
                slotProps={{
                  htmlInput: {
                    inputMode: 'numeric',
                    maxLength: 1,
                    'aria-label': `Digit ${index + 1} of ${length}`,
                    style: { textAlign: 'center', fontSize: '1.25rem', width: 20 },
                  },
                }}
                onChange={(event) => {
                  const char = event.target.value.replace(/\D/g, '').slice(-1);
                  setDigit(index, char);
                  if (char && index < length - 1) {
                    inputRefs.current[index + 1]?.focus();
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Backspace' && !digits[index].trim() && index > 0) {
                    inputRefs.current[index - 1]?.focus();
                  }
                }}
              />
            ))}
          </Stack>
        );
      }}
    />
  );
}
